import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface ShopObject {
  nameEn: string;
  nameAe: string;
  phone: string;
  latitude: string;
  longitude: string;
  id: string;
}

export interface ShopListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  shop: Array<ShopObject>;
  errors: unknown;
}

export interface ShopFilterInfo {
  name: string;
}

@Module
export default class ShopModule extends VuexModule implements ShopListInfo {
  shop = [] as Array<ShopObject>;
  allShop = [] as Array<ShopObject>;
  shopFilter = {} as ShopFilterInfo;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get shop list
   * @returns Array<ShopObject>
   */
  get shopList(): Array<ShopObject> {
    return this.shop;
  }

  get allShopList(): Array<ShopObject> {
    return this.allShop;
  }

  /**
   * Get total items
   * @returns number
   */
  get shopListCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_SHOPS](data) {
    this.shop = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_SHOPS](data) {
    this.allShop = data.data;
  }

  @Mutation
  [Mutations.SET_SHOP_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_SHOP_LIST]() {
    this.shop = [] as Array<ShopObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_SHOP_FILTER](obj) {
    this.shopFilter.name = obj.name;
  }

  @Action
  [Actions.RESET_SHOP_STORE]() {
    this.context.commit(Mutations.RESET_SHOP_LIST);
  }

  @Action
  [Actions.UPDATE_SHOP_FILTER](val) {
    this.context.commit(Mutations.SET_SHOP_FILTER, val);
  }

  @Action
  [Actions.CREATE_SHOP](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/shop', data)
      .then((data) => {
        this.context.commit(Mutations.RESET_SHOP_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPLOAD_SHOP_ATTACHMENT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/shop/attachments/${id}`, data)
      .then(() => {
        this.context.commit(Mutations.RESET_GROOMERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_SHOP](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/shop', id)
      .then(() => {
        this.context.commit(Mutations.RESET_SHOP_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_SHOP]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/shop', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_SHOP_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_SHOP_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_SHOP_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_SHOP](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/shop', id);
  }

  @Action
  [Actions.GET_SHOPS]() {
    const params = {
      page: this.page,
      name: this.shopFilter.name,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/shop', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_SHOPS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_ALL_SHOP]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get(`/shop/all`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_SHOPS, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
